import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import { Icon } from '@iconify/react';

import { skillsStore } from 'stores/skillsStore';
import { observer } from 'mobx-react-lite';
import ErrorScreen from 'components/ErrorScreen';
import { interviewStore } from 'stores/interviewStore';
import { personalDataStore } from 'stores/personalDataStore';
import { ExctractResults } from 'types/skills';
import LoadingScreen from 'components/LoadingScreen';
import { omit } from 'lodash';
import { userApi } from 'api';
import { useSocketContext } from 'contexts/SocketContext';
import { PATHS } from 'routes/paths';
import SkillLevelIntroModal from 'components/SkillLevel/SkillLevelIntroModal';

// Описания уровней навыков
const skillLevelDescriptions = {
  0: 'Новичок: Базовое понимание концепций',
  1: 'Начинающий: Знаком с основами, требуется постоянная поддержка',
  2: 'Развивающийся: Может выполнять простые задачи самостоятельно',
  3: 'Продвинутый начинающий: Работает над средними задачами с поддержкой',
  4: 'Компетентный: Самостоятельно решает типовые задачи',
  5: 'Опытный: Хорошо разбирается в большинстве аспектов',
  6: 'Продвинутый: Глубокое понимание и опыт работы',
  7: 'Профессионал: Эксперт в своей области',
  8: 'Мастер: Может обучать других и решать сложные задачи',
  9: 'Эксперт: Признанный специалист с глубокими знаниями'
} as const;

// Ссылка на пример полного PDF-отчёта.
// Положите файл с примером отчёта в public/static/ (или замените путь на актуальный).
const SAMPLE_REPORT_URL = 'https://storage.yandexcloud.net/aihr-s3/report%20(36)%20(1).pdf';

// Превью того, что пользователь получит, пройдя полное интервью.
// Отражает реальные разделы итогового отчёта (см. components/InterviewResults).
const FULL_REPORT_FEATURES = [
  {
    icon: 'mdi:trending-up',
    title: 'Рекомендации по росту',
    description: 'Конкретные советы, что улучшить и куда развиваться дальше'
  },
  {
    icon: 'mdi:chart-bar',
    title: 'Оценка навыков по 10-балльной шкале',
    description: 'Объективный балл по каждому навыку рядом с вашей самооценкой'
  },
  {
    icon: 'mdi:account-heart-outline',
    title: 'Личностный профиль Big Five',
    description: 'Открытость, сознательность, экстраверсия и другие черты'
  },
  {
    icon: 'mdi:cash-multiple',
    title: 'Зарплатный ориентир',
    description: 'Рекомендуемая вилка для Москвы, Санкт-Петербурга и регионов'
  },
  // {
  //   icon: 'mdi:microphone-message',
  //   title: 'Анализ речи и видеоответы',
  //   description: 'Оценка ясности и сложности речи по каждому ответу'
  // },
  {
    icon: 'mdi:file-document-check-outline',
    title: 'PDF-отчёт для работодателя',
    description: 'Готовый документ, который можно скачать и отправить'
  },
  {
    icon: 'mdi:target-account',
    title: 'Соответствие должности',
    description: 'Итоговая оценка и вывод, подходите ли вы под позицию'
  }
] as const;

interface FormattedDescription {
  intro: string;
  skills: Array<{ title: string; description: string }>;
  overallConclusion: string;
}

const formatDescription = (data: Omit<ExctractResults, 'competences'>): FormattedDescription => ({
  intro: data.summary,
  overallConclusion: data.overall_conclusion.text,
  skills:
    data.sections?.map((section) => ({
      title: section.title,
      description: section.description
    })) || []
});

const GRADE_BUTTON_SX = {
  width: { xs: 34, md: 44 },
  minWidth: { xs: 34, md: 44 },
  height: { xs: 34, md: 44 },
  minHeight: { xs: 34, md: 44 }
} as const;

interface GradeButtonsProps {
  isMobile: boolean;
  onSelect: (grade: number) => void;
  selectedGrade: number | undefined;
}

const GradeButtons = ({ isMobile, onSelect, selectedGrade }: GradeButtonsProps) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    width={isMobile ? '100%' : '70%'}
    spacing={isMobile ? 0 : 8}
  >
    {Array.from({ length: 10 }).map((_, index) => (
      <Box key={index} sx={{ position: 'relative' }}>
        <Tooltip
          title={skillLevelDescriptions[index as keyof typeof skillLevelDescriptions]}
          placement="bottom"
          arrow
          enterDelay={200}
          leaveDelay={0}
          open={isMobile ? selectedGrade === index + 1 : undefined}
        >
          <Button
            sx={GRADE_BUTTON_SX}
            variant={
              selectedGrade !== undefined && selectedGrade > index ? 'contained' : 'outlined'
            }
            onClick={() => onSelect(index)}
          >
            {index + 1}
          </Button>
        </Tooltip>
      </Box>
    ))}
  </Stack>
);

const SkillLevelPage = () => {
  const navigate = useNavigate();
  const { id: interviewId } = useParams();
  const [currentSkillIndex, setCurrentSkillIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [showDescription, setShowDescription] = useState(true);
  const [showIntroModal, setShowIntroModal] = useState(true);
  const { userId } = personalDataStore;
  const { socket } = useSocketContext();

  const [isSubmitted, setIsSubmitted] = useState(false);

  const error = skillsStore.error;

  const totalSkills = skillsStore.skills?.length;

  const currentSkill = skillsStore.skills ? skillsStore.skills[currentSkillIndex] : '';
  const currentGrade = skillsStore.skillsWithGrade?.[currentSkill];

  const handleError = (error: string) => {
    skillsStore.error = error;
  };

  const extractedInterview = useMemo(() => {
    return personalDataStore.extractInterviews.find((i) => i.interviewId === interviewId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, personalDataStore.extractInterviews]);

  // useEffect(() => {
  //   if(!extractedInterview?.vacancyId && process.env.REACT_APP_IS_HR_DEMO === "true") {
  //     navigate('/main')
  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [extractedInterview])

  useEffect(() => {
    return () => {
      skillsStore.extractResults = null;
      skillsStore.questions = [];
    };
  }, []);

  useEffect(() => {
    if (!interviewId || !extractedInterview) {
      navigate(-1);
      return;
    }

    skillsStore.authInterviewId = extractedInterview.id;

    if (Object.keys(extractedInterview.results || {}).length) {
      skillsStore.skills = extractedInterview.results.competences;
      skillsStore.extractResults = omit(
        extractedInterview.results as ExctractResults,
        'competences'
      );
      return;
    }

    // Результаты уже в сторе (пришли по WS в этой сессии) — не запускаем заново.
    if (skillsStore.skills.length) {
      return;
    }

    if (extractedInterview.isAnswered || extractedInterview.index >= 3) {
      skillsStore.getSkills(interviewId, socket);
      return;
    }
    handleError('Не удалось получить результаты');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, navigate, socket, extractedInterview]);

  const formattedDescription = useMemo(() => {
    if (!skillsStore.extractResults?.summary) return null;
    return formatDescription(skillsStore.extractResults);
    // skillsStore — MobX store, observer обеспечивает ре-рендер при изменении extractResults
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillsStore.extractResults]);

  const handleGradeSelect = async (grade: number, skill?: string) => {
    try {
      await skillsStore.setSkillsWithGrade(skill || currentSkill, grade + 1);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePrevSkill = () => {
    if (currentSkillIndex <= 0) return;
    setCurrentSkillIndex((i) => i - 1);
  };

  const handleNextSkill = () => {
    if (currentGrade === undefined || totalSkills == null || currentSkillIndex + 1 >= totalSkills)
      return;
    setCurrentSkillIndex((i) => i + 1);
  };

  useEffect(() => {
    if (!isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentSkillIndex > 0) {
        setCurrentSkillIndex((i) => i - 1);
      } else if (
        e.key === 'ArrowRight' &&
        currentGrade !== undefined &&
        totalSkills != null &&
        currentSkillIndex + 1 < totalSkills
      ) {
        setCurrentSkillIndex((i) => i + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, currentSkillIndex, currentGrade, totalSkills]);

  const toInterview = async () => {
    setIsSubmitted(true);
    if (!userId || !interviewId) {
      handleError('Не удалось подготовить интервью');
      setIsSubmitted(false);
      return;
    }
    await userApi.updateExtractInterview(skillsStore.authInterviewId as string, {
      finish: true
    });
    personalDataStore.getExtractInterviews();
    if (extractedInterview && extractedInterview.vacancyId) {
      await interviewStore.startInterview(interviewId as string, extractedInterview.vacancyId);
      navigate(
        `/requested-interviews/video-interview/record/${
          interviewStore.currentInterview?.id as string
        }`
      );
      setIsSubmitted(false);
      return;
    }
    // Оплата больше не требуется перед интервью — она запрашивается
    // на странице результатов, чтобы открыть полный отчёт.
    await interviewStore.startInterview(interviewId as string);

    if (interviewStore.currentInterview?.id) {
      navigate(`/video-interview/record/${interviewStore.currentInterview.id}`);
    } else {
      handleError('Не удалось создать интервью');
    }
    setIsSubmitted(false);
  };

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (skillsStore.loading) {
    return (
      <LoadingScreen label="Определям ваши навыки, пожалуйста, не покидайте страницу, до загрузки результатов" />
    );
  }

  // Показываем экран с описанием перед показом навыков
  if (showDescription && formattedDescription) {
    return (
      <Stack
        alignItems="center"
        sx={{ height: 'calc(100% - 60px)', overflowY: 'auto', py: { xs: 2, md: 4 } }}
        gap={3}
      >
        <Box
          sx={{
            // maxWidth: '780px',
            maxWidth: 890,
            width: '100%',
            // p: { xs: 2.5, md: 5 },
            pb: 0
            // boxShadow: { xs: 'none', md: '0 12px 40px rgba(15, 23, 42, 0.08)' }
          }}
        >
          <Stack spacing={4}>
            {/* Хедер — «зацепка» */}
            <Stack spacing={1.5}>
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.16),
                  color: 'success.dark'
                }}
              >
                <Icon icon="mdi:check-decagram" width={16} height={16} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Предварительный анализ готов
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Вы увидели лишь верхушку айсберга
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Это интервью было ознакомительным — чтобы вы познакомились с системой и увидели, как
                она работает. Поэтому сейчас доступен лишь краткий обзор.{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Полноценная интерпретация
                </Box>{' '}
                — объективная оценка каждого навыка, личностный профиль и зарплатный ориентир —
                откроется только после прохождения полного интервью.
              </Typography>
            </Stack>

            {/* Краткий вывод */}
            {formattedDescription.intro && (
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 3,
                  p: { xs: 2.5, md: 3 }
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: 'text.secondary',
                    mb: 1.5
                  }}
                >
                  Краткий вывод
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.6 }}>
                  {formattedDescription.intro}
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.6 }}>
                  {formattedDescription.overallConclusion}
                </Typography>
              </Box>
            )}

            <Button
              size="large"
              variant="contained"
              startIcon={<Icon icon="mdi:rocket-launch-outline" width={20} height={20} />}
            >
              Отличное начало! Раскройте полную картину о себе
            </Button>

            {/* Навыки */}
            {/* {formattedDescription.skills.length > 0 && (
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      color: 'text.secondary',
                      mb: 0.5
                    }}
                  >
                    Ваши ключевые навыки
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Собраны из профилей специалистов с похожей должностью и опытом и из короткой
                    беседы с вами. По ним сформируем вопросы для полного интервью.
                  </Typography>
                </Box>
                {formattedDescription.skills.map((skill, index) => (
                  <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            mt: '7px',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            flexShrink: 0
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}
                        >
                          {skill.title}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {skill.description}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )} */}

            {/* Полная версия — что внутри отчёта */}
            <Box
              sx={{
                borderRadius: 3,
                p: { xs: 2.5, md: 3.5 },
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
                    theme.palette.primary.light,
                    0.1
                  )} 100%)`
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Icon icon="mdi:lock-open-variant-outline" width={22} height={22} color="#6558F5" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Что откроется в полном отчёте
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Пройдите интервью до конца, чтобы получить:
              </Typography>

              <Grid container spacing={2}>
                {FULL_REPORT_FEATURES.map((feature) => (
                  <Grid item xs={12} sm={6} key={feature.title}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{
                        height: '100%',
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'grey.300'
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main'
                        }}
                      >
                        <Icon icon={feature.icon} width={22} height={22} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              <Button
                component="a"
                href={SAMPLE_REPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                fullWidth
                color="secondary"
                startIcon={<Icon icon="mdi:file-pdf-box" />}
                sx={{ mt: 2.5, textTransform: 'none', fontWeight: 600 }}
              >
                Посмотреть пример полного отчёта (PDF)
              </Button>
            </Box>
            <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Button
                variant="contained"
                onClick={() => setShowDescription(false)}
                size="large"
                endIcon={<Icon icon="mdi:arrow-right" />}
                sx={{ minWidth: { xs: '100%', sm: 320 }, textTransform: 'none', fontWeight: 700 }}
              >
                Перейти к полному интервью
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Следующий шаг — быстрая самооценка навыков, затем интервью
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    );
  }

  if (totalSkills === 0) {
    return (
      <LoadingScreen label="Определям ваши навыки, пожалуйста, не покидайте страницу, до загрузки результатов" />
    );
  }

  const allSkillsGraded =
    skillsStore.skills?.every((s) => skillsStore.skillsWithGrade[s] !== undefined) || false;
  const isFinishDisabled = isMobile ? currentGrade === undefined : !allSkillsGraded;

  return (
    <Stack
      justifyContent="space-between"
      flexDirection={isMobile ? 'column' : 'row'}
      sx={{ height: '100%', gap: 3 }}
    >
      <SkillLevelIntroModal open={showIntroModal} onConfirm={() => setShowIntroModal(false)} />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack sx={{ mb: 4 }} spacing={4}>
          {isMobile && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={totalSkills ? ((currentSkillIndex + 1) / totalSkills) * 100 : 0}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Навык {currentSkillIndex + 1}/{totalSkills}
              </Typography>
            </Box>
          )}
          {isMobile && (
            <Typography textAlign="center" variant="h5" sx={{ mb: 2 }}>
              {currentSkill}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" align="center">
            Укажите ваш уровень владения навыком по шкале ниже, где слева — самый низкий уровень
            (новичок), а справа — самый высокий уровень (профессионал)
          </Typography>
        </Stack>

        <Stack
          sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}
          spacing={6}
          alignItems="center"
          width="100%"
        >
          {isMobile ? (
            <GradeButtons
              isMobile={true}
              onSelect={(grade) => handleGradeSelect(grade)}
              selectedGrade={currentGrade}
            />
          ) : (
            <Stack spacing={3} sx={{ height: 'calc(100vh - 280px)', overflow: 'auto', pb: 6 }}>
              {skillsStore.skills.map((skill) => (
                <Box key={skill}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {skill}
                  </Typography>
                  <GradeButtons
                    isMobile={false}
                    onSelect={(grade) => handleGradeSelect(grade, skill)}
                    selectedGrade={skillsStore.skillsWithGrade[skill]}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Stack>

        <Stack spacing={2} sx={{ marginTop: 'auto' }} direction="row">
          {isMobile && currentSkillIndex !== 0 && (
            <Button
              variant="outlined"
              fullWidth
              onClick={handlePrevSkill}
              size="large"
              color="info"
              startIcon={<Icon icon="mdi:arrow-left" />}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Предыдущий
            </Button>
          )}

          {isMobile && currentSkillIndex + 1 < totalSkills ? (
            <Button
              variant="outlined"
              fullWidth
              onClick={handleNextSkill}
              size="large"
              disabled={currentGrade === undefined}
              endIcon={<Icon icon="mdi:arrow-right" />}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Следующий
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={toInterview}
              size="large"
              disabled={isFinishDisabled}
              endIcon={<Icon icon="material-symbols:check-circle-outline" />}
              color="primary"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Завершить
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default observer(SkillLevelPage);
